import React, { useState, useEffect, useRef } from 'react';
import { Grid, IOption } from '../../../../base';
import {
    StyledBox,
    StyledBoxText,
    StyledScrollContainer,
} from './SingleSelectionAction.styles';

interface SingleSelectionActionProps {
    options: IOption[];
    onSelectionChange: (value: IOption) => void;
}

const SingleSelectionAction: React.FC<SingleSelectionActionProps> = ({
    options,
    onSelectionChange,
}) => {
    const [selectedValue, setSelectedValue] = useState<IOption | null>(null);

    const handleSelect = (option: IOption) => {
        setSelectedValue(option);
        onSelectionChange(option);
    };

    return (
        <StyledScrollContainer>
            <Grid container gap={2}>
                {options.map((option, index) => (
                    <Grid item>
                        <BoxItem
                            key={option.key || option.value || index}
                            option={option}
                            isSelected={selectedValue?.value === option.value}
                            onSelect={handleSelect}
                        />
                    </Grid>
                ))}
            </Grid>
        </StyledScrollContainer>
    );
};

interface BoxItemProps {
    option: IOption;
    isSelected: boolean;
    onSelect: (option: IOption) => void;
}

const BoxItem: React.FC<BoxItemProps> = ({ option, isSelected, onSelect }) => {
    const textRef = useRef<HTMLDivElement>(null);
    const boxRef = useRef<HTMLButtonElement>(null);
    const [fontSize, setFontSize] = useState(16); // Initial font size

    useEffect(() => {
        const adjustFontSize = () => {
            if (textRef.current && boxRef.current) {
                const box = boxRef.current;
                const text = textRef.current;

                // Reset font size to ensure scrollHeight is calculated correctly
                text.style.fontSize = '16px';
                let currentFontSize = 16;
                const padding = 16;
                const maxHeight = box.clientHeight - padding;
                const maxWidth = box.clientWidth - padding;

                // Reduce font size until text fits
                while (
                    (text.scrollHeight > maxHeight ||
                        text.scrollWidth > maxWidth) &&
                    currentFontSize > 10 // Minimum font size
                ) {
                    currentFontSize -= 1;
                    text.style.fontSize = `${currentFontSize}px`;
                }
                setFontSize(currentFontSize);
            }
        };

        adjustFontSize();
        window.addEventListener('resize', adjustFontSize);
        return () => window.removeEventListener('resize', adjustFontSize);
    }, [option.label]);

    return (
        <StyledBox
            ref={boxRef}
            isSelected={isSelected}
            onClick={() => onSelect(option)}
        >
            <StyledBoxText ref={textRef} style={{ fontSize: `${fontSize}px` }}>
                {option.label}
            </StyledBoxText>
        </StyledBox>
    );
};

export default SingleSelectionAction;
